import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Download, TrendingUp, MinusCircle, Truck, MessageSquareWarning, PiggyBank, Eye } from 'lucide-react';
import { openReceipt } from '../components/FuelReceiptUploader';
import DashboardLayout from '../components/DashboardLayout';
import { toast } from '../components/ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAY_TYPE_LABELS = {
  salary: 'Salary',
  per_mile: 'Per Mile',
  hourly: 'Hourly',
  percentage: 'Percentage',
};

const DEDUCTION_LABELS = {
  // New permanent labels
  eld: 'ELD',
  parking: 'Parking',
  insurance: 'Insurance',
  lease_on: 'Lease-on',
  trailer_rental: 'Trailer Rental',
  dispatch_fee: 'Dispatch Fee',
  factoring_fee: 'Factoring Fee',
  // Legacy
  truck_lease: 'Truck Lease Fee', fuel: 'Fuel Expense', other: 'Other Deduction',
  monthly_parking: 'Monthly Parking',
  daily_parking: 'Daily Parking',
  eld_fee: 'ELD Fee',
  toll: 'Toll',
  truck_wash: 'Truck Wash',
  repair: 'Repair',
  lease_on_fee: 'Lease-on Fee',
  trailer_fee: 'Trailer Fee',
};

const EXPENSE_CATEGORY_LABEL = {
  fuel: 'Fuel', toll: 'Toll', truck_wash: 'Truck Wash', repair: 'Repair',
  trip_repair: 'Trip Repair', ifta_tax: 'IFTA Tax', ifta_filing: 'IFTA Filing',
  plate_renewal: 'Plate Renewal', permit: 'Permit', drug_test: 'Drug Test',
  tax_2290: '2290 Tax', advance_repayment: 'Advance Repayment', other: 'Other',
};

const fmtMoney = (n) =>
  `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Net pay is shown in RED when negative (spec Section 9).
const netColor = (n) => ((Number(n) || 0) < 0 ? 'text-red-600' : 'text-green-700');

const CATEGORY_LABELS = {
  eld: 'ELD', parking: 'Parking', insurance: 'Insurance', lease_on: 'Lease-on',
  trailer_rental: 'Trailer Rental', dispatch_fee: 'Dispatch Fee', factoring_fee: 'Factoring Fee',
  escrow: 'Escrow', legacy_balance: 'Prior Balance', toll: 'Toll', truck_wash: 'Truck Wash',
  repair: 'Repair', standing: 'Standing Deduction',
};

const REFUND_REASON_LABELS = {
  overpayment: 'Overpayment', contract_end: 'Contract End', adjustment: 'Adjustment', other: 'Other',
};
const refundTag = (rf) => REFUND_REASON_LABELS[rf.reason] || rf.notes;

const KpiCard = ({ testid, label, value, accent, sub }) => (
  <div data-testid={testid} className="bg-white border border-slate-200 rounded-md p-4">
    <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{label}</p>
    <p className={`text-2xl font-bold ${accent || 'text-slate-900'}`} style={{ fontFamily: 'Work Sans, sans-serif' }}>{value}</p>
    {sub && <p className="text-xs text-slate-500 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{sub}</p>}
  </div>
);

const MyPay = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);
  const [correctionsByEntry, setCorrectionsByEntry] = useState({});
  const [escrow, setEscrow] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/payrolls/me`, { withCredentials: true });
      setData(res.data);
      try {
        const eRes = await axios.get(`${API}/my/escrow`, { withCredentials: true });
        setEscrow(eRes.data?.config || null);
      } catch (err) { console.error('[MyPay] escrow load (non-fatal)', err); }
      // Fetch the caller's corrections so we can show a per-row status badge
      try {
        const cRes = await axios.get(`${API}/payrolls/me/corrections`, { withCredentials: true });
        const byEntry = {};
        (cRes.data || []).forEach((c) => {
          const list = byEntry[c.entry_id] || [];
          list.push(c);
          byEntry[c.entry_id] = list;
        });
        setCorrectionsByEntry(byEntry);
      } catch (err) { console.error('[MyPay] corrections load (non-fatal)', err); }
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Failed to load your pay history');
    } finally {
      setLoading(false);
    }
  };

  const correctionBadgeFor = (entryId) => {
    const items = correctionsByEntry[entryId] || [];
    if (items.length === 0) return null;
    // Show the most actionable status: any pending → Pending; else if any approved → Approved; else Rejected
    const priority = ['pending', 'approved', 'rejected'];
    const status = priority.find((s) => items.some((i) => i.status === s));
    if (!status) return null;
    const cfg = {
      pending: 'bg-amber-100 text-amber-800',
      approved: 'bg-emerald-100 text-emerald-800',
      rejected: 'bg-red-100 text-red-800',
    }[status];
    return (
      <span data-testid={`row-correction-${status}-${entryId}`} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest ${cfg}`}>
        <MessageSquareWarning className="w-3 h-3" />
        {status}
      </span>
    );
  };

  const handleDownloadPaystub = async (entry) => {
    setDownloadingId(entry.entry_id);
    try {
      const res = await axios.get(
        `${API}/payrolls/${entry.payroll_id}/entries/${entry.entry_id}/paystub.pdf`,
        { withCredentials: true, responseType: 'blob' }
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `paystub_${entry.week_end}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to download paystub');
    } finally {
      setDownloadingId(null);
    }
  };

  if (loading) {
    return <DashboardLayout><div className="p-6 md:p-8" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Loading...</div></DashboardLayout>;
  }
  if (error) {
    return <DashboardLayout><div className="p-6 md:p-8 text-red-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{error}</div></DashboardLayout>;
  }

  const { entries = [], current, ytd = {}, year } = data || {};

  return (
    <DashboardLayout>
      <div data-testid="my-pay-page" className="p-4 md:p-8 max-w-5xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>My Pay</h1>
          <p className="text-sm text-slate-600 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Past, current, and year-to-date earnings & deductions.</p>
        </div>

        {/* YTD SUMMARY */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-4 h-4 text-[#002FA7]" />
            <h2 className="text-base font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Year-to-Date · {year}</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard testid="ytd-gross" label="Gross" value={fmtMoney(ytd.gross)} accent="text-slate-900" sub={`${ytd.load_count || 0} loads`} />
            <KpiCard testid="ytd-deductions" label="Deductions" value={fmtMoney((ytd.standing_deductions || 0) + (ytd.advance_repayments || 0) + (ytd.custom_deductions || 0))} accent="text-orange-700" sub="Standing + advances + custom" />
            <KpiCard testid="ytd-escrow" label="Escrow Collected" value={fmtMoney(ytd.escrow_collected)} accent="text-[#002FA7]" sub="Into escrow YTD" />
            <KpiCard testid="ytd-net" label="Net Pay" value={fmtMoney(ytd.net)} accent={netColor(ytd.net)} sub="Take-home" />
          </div>
          {(ytd.deferred_outstanding > 0) && (
            <div className="mt-3">
              <KpiCard testid="ytd-deferred" label="Deferred Owed (as of now)" value={fmtMoney(ytd.deferred_outstanding)} accent="text-red-600" sub="Carries to future paychecks" />
            </div>
          )}
        </div>

        {/* ESCROW PROGRESS */}
        {escrow && escrow.total_required > 0 && (() => {
          const pct = Math.min(100, Math.round((escrow.collected_to_date / escrow.total_required) * 100));
          const done = escrow.status === 'completed' || escrow.remaining <= 0;
          return (
            <div data-testid="escrow-progress-card" className="bg-white border border-slate-200 rounded-md p-6 mb-8">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <PiggyBank className="w-4 h-4 text-[#002FA7]" />
                  <h2 className="text-base font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Escrow Balance</h2>
                  {done && <span className="text-[11px] font-semibold uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-0.5">Fully funded</span>}
                  {escrow.status === 'settled' && <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-600 bg-slate-100 border border-slate-200 rounded px-2 py-0.5">Settled</span>}
                </div>
                <div className="flex items-center gap-3">
                  <button data-testid="my-escrow-statement-btn" onClick={async () => {
                    try {
                      const r = await axios.get(`${API}/my/escrow/statement.pdf`, { withCredentials: true, responseType: 'blob' });
                      const u = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
                      window.open(u, '_blank');
                      setTimeout(() => window.URL.revokeObjectURL(u), 60000);
                    } catch (err) { console.error('[MyPay] statement download failed', err); }
                  }} className="inline-flex items-center gap-1 text-xs font-semibold text-[#002FA7] border border-[#002FA7] rounded-md px-2.5 py-1.5 hover:bg-blue-50">
                    <Download className="w-3.5 h-3.5" /> Statement
                  </button>
                  <p className="text-sm font-bold text-slate-900" data-testid="escrow-progress-amount">{fmtMoney(escrow.collected_to_date)} <span className="text-slate-400 font-normal">of</span> {fmtMoney(escrow.total_required)}</p>
                </div>
              </div>
              <div className="w-full h-4 rounded-full bg-slate-100 overflow-hidden">
                <div data-testid="escrow-progress-bar" className={`h-full rounded-full transition-all duration-500 ${done ? 'bg-emerald-500' : 'bg-[#002FA7]'}`} style={{ width: `${pct}%` }} />
              </div>
              <div className="flex items-center justify-between mt-2 text-xs text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                <span>{pct}% collected</span>
                <span>{fmtMoney(escrow.remaining)} remaining · {fmtMoney(escrow.per_period_amount)}/period</span>
              </div>
            </div>
          );
        })()}

        {/* CURRENT PERIOD */}
        {current ? (
          <div data-testid="current-period-card" className="bg-white border border-slate-200 rounded-md p-6 mb-8">
            <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-[#002FA7] mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Current Period</p>
                <h3 className="text-xl font-bold tracking-tight text-slate-900 flex items-center gap-2 flex-wrap" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                  <Calendar className="w-5 h-5 text-slate-500" />
                  {current.week_start} → {current.week_end}
                  {correctionBadgeFor(current.entry_id)}
                </h3>
                <p className="text-xs text-slate-500 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  {current.entry_kind === 'staff'
                    ? `Salary · $${Number(current.pay_rate).toLocaleString(undefined, { minimumFractionDigits: 2 })} / week · Office staff`
                    : <>{current.pay_type ? `${PAY_TYPE_LABELS[current.pay_type]} @ ${current.pay_type === 'percentage' ? `${current.pay_rate}%` : `$${current.pay_rate}`}` : 'No pay structure'}
                  {' · '}{current.load_count} load{current.load_count === 1 ? '' : 's'}</>}
                </p>
              </div>
              <button
                data-testid="download-current-paystub"
                onClick={() => handleDownloadPaystub(current)}
                disabled={downloadingId === current.entry_id}
                className="flex items-center gap-2 bg-[#002FA7] text-white px-4 py-2 rounded-md font-semibold hover:bg-[#00247D] transition-all disabled:opacity-50"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                <Download className="w-4 h-4" />
                {downloadingId === current.entry_id ? 'Downloading…' : 'Download Paystub'}
              </button>
              <Link
                data-testid="view-current-detail"
                to={`/my-pay/${current.entry_id}`}
                className="flex items-center gap-2 bg-white text-[#002FA7] border border-[#002FA7] px-4 py-2 rounded-md font-semibold hover:bg-blue-50 transition-all"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                View Details & Request Correction
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <KpiCard testid="current-gross" label="Gross" value={fmtMoney(current.gross_pay)} />
              <KpiCard testid="current-comp" label="+ Comp" value={fmtMoney(current.custom_compensation_total)} accent="text-blue-700" />
              <KpiCard testid="current-standing" label="− Standing" value={fmtMoney(current.standing_deductions_total)} accent="text-orange-700" />
              <KpiCard testid="current-advance" label="− Advances" value={fmtMoney(current.advance_repayments_total)} accent="text-orange-700" />
              <KpiCard testid="current-net" label="= Net" value={fmtMoney(current.net_pay)} accent={netColor(current.net_pay)} />
            </div>
            {current.net_pay < 0 && (
              <div data-testid="current-negative-net-banner" className="mt-3 p-3 border border-red-200 bg-red-50 rounded-md text-sm text-red-800" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                Your deductions exceeded your earnings this period. Net pay is <span className="font-bold">{fmtMoney(current.net_pay)}</span>. The uncovered amount carries forward and will be recovered from future paychecks.
              </div>
            )}

            {/* Escrow snapshot */}
            {current.escrow_snapshot && (
              <div data-testid="current-escrow-card" className="mt-6 border-t border-slate-200 pt-4">
                <h4 className="text-sm font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Work Sans, sans-serif' }}>Escrow</h4>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  <KpiCard testid="escrow-required" label="Required" value={fmtMoney(current.escrow_snapshot.required)} />
                  <KpiCard testid="escrow-collected-before" label="Collected Before" value={fmtMoney(current.escrow_snapshot.collected_before)} accent="text-emerald-700" />
                  <KpiCard testid="escrow-current" label="This Period" value={fmtMoney(current.escrow_snapshot.current_collected)} accent="text-[#002FA7]" />
                  <KpiCard testid="escrow-remaining" label="Remaining" value={fmtMoney(current.escrow_snapshot.remaining_after)} accent="text-orange-700" />
                  <KpiCard testid="escrow-status" label="Status" value={current.escrow_snapshot.status} />
                </div>
                {current.escrow_snapshot.current_deferred > 0 && (
                  <p className="text-xs text-red-600 mt-2">Escrow deferred this period: {fmtMoney(current.escrow_snapshot.current_deferred)} (carried forward).</p>
                )}
              </div>
            )}

            {/* Escrow refunds (positive payout) */}
            {(current.escrow_refunds_applied && current.escrow_refunds_applied.length > 0) && (
              <div data-testid="current-escrow-refund-card" className="mt-6 border-t border-slate-200 pt-4">
                <h4 className="text-sm font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Work Sans, sans-serif' }}>Escrow Refunds</h4>
                {current.escrow_refunds_applied.map((rf, i) => (
                  <div key={`rf${i}`} className="flex items-center justify-between text-sm py-1 border-b border-slate-100 last:border-0">
                    <span className="text-slate-700">Escrow refund{refundTag(rf) ? <span className="text-xs text-slate-500 ml-2">{refundTag(rf)}</span> : null}</span>
                    <span className="text-emerald-700 font-semibold">+ {fmtMoney(rf.amount)}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Deferred deductions */}
            {((current.deferred_applied && current.deferred_applied.length > 0) || (current.deferred_created && current.deferred_created.length > 0)) && (
              <div data-testid="current-deferred-card" className="mt-6 border-t border-slate-200 pt-4">
                <h4 className="text-sm font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Work Sans, sans-serif' }}>Deferred Deductions</h4>
                {current.deferred_applied && current.deferred_applied.length > 0 && (
                  <div className="mb-2">
                    <p className="text-[11px] uppercase tracking-widest text-emerald-700 mb-1">Prior deferred settled this period</p>
                    {current.deferred_applied.map((d, i) => (
                      <div key={`da${i}`} className="flex items-center justify-between text-sm py-1 border-b border-slate-100 last:border-0">
                        <span className="text-slate-700">{CATEGORY_LABELS[d.category] || d.category}{d.source_period_end ? <span className="text-xs text-slate-500 ml-2">from {d.source_period_end}</span> : null}</span>
                        <span className="text-emerald-700 font-semibold">− {fmtMoney(d.amount)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {current.deferred_created && current.deferred_created.length > 0 && (
                  <div>
                    <p className="text-[11px] uppercase tracking-widest text-red-700 mb-1">Deferred to next period (insufficient earnings)</p>
                    {current.deferred_created.map((d, i) => (
                      <div key={`dc${i}`} className="flex items-center justify-between text-sm py-1 border-b border-slate-100 last:border-0">
                        <span className="text-slate-700">{CATEGORY_LABELS[d.category] || d.category}</span>
                        <span className="text-red-600 font-semibold">{fmtMoney(d.amount)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Carry-forward balance owed (if any) */}
            {(current.prior_balance_owed > 0 || current.new_balance_owed > 0 || current.balance_paydown > 0) && (
              <div data-testid="current-balance-owed" className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="border border-amber-200 bg-amber-50 rounded-md p-3">
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-amber-700 mb-1">Prior Balance Owed</p>
                  <p className="text-xl font-bold text-amber-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{fmtMoney(current.prior_balance_owed)}</p>
                  <p className="text-[11px] text-amber-700 mt-1">Carried in from last period</p>
                </div>
                <div className="border border-emerald-200 bg-emerald-50 rounded-md p-3">
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-emerald-700 mb-1">Balance Paid Down</p>
                  <p className="text-xl font-bold text-emerald-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{fmtMoney(current.balance_paydown)}</p>
                  <p className="text-[11px] text-emerald-700 mt-1">Recovered this period</p>
                </div>
                <div className={`border rounded-md p-3 ${current.new_balance_owed > 0 ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-slate-50'}`}>
                  <p className={`text-[11px] font-semibold uppercase tracking-widest mb-1 ${current.new_balance_owed > 0 ? 'text-red-700' : 'text-slate-500'}`}>Carries To Next</p>
                  <p className={`text-xl font-bold ${current.new_balance_owed > 0 ? 'text-red-900' : 'text-slate-700'}`} style={{ fontFamily: 'Work Sans, sans-serif' }}>{fmtMoney(current.new_balance_owed)}</p>
                  <p className={`text-[11px] mt-1 ${current.new_balance_owed > 0 ? 'text-red-700' : 'text-slate-500'}`}>{current.new_balance_owed > 0 ? 'Will deduct from next paycheck' : 'Fully settled'}</p>
                </div>
              </div>
            )}

            {/* Deductions breakdown */}
            {((current.standing_deductions && current.standing_deductions.length > 0)
              || current.admin_fee_reason
              || (current.advance_repayments && current.advance_repayments.length > 0)
              || (current.adjustments && current.adjustments.length > 0)) && (
              <div data-testid="current-deductions-breakdown" className="mt-6 border-t border-slate-200 pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <MinusCircle className="w-4 h-4 text-orange-600" />
                  <h4 className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Deductions & Adjustments</h4>
                </div>
                <div className="space-y-1.5">
                  {(current.standing_deductions || []).map((sd, i) => (
                    <div key={`sd-${i}`} data-testid={`current-standing-row-${i}`} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-100 last:border-0" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      <span className="text-slate-700">
                        {DEDUCTION_LABELS[sd.deduction_type] || sd.deduction_type}
                        {sd.txn_date ? (
                          <span className="text-xs text-slate-500 ml-2">({[new Date(`${sd.txn_date}T00:00:00`).toLocaleDateString(), sd.description, sd.truck_unit && `Unit ${sd.truck_unit}`, sd.gallons && `${sd.gallons} gal`, sd.receipt_ref && `Receipt ${sd.receipt_ref}`].filter(Boolean).join(' · ')})</span>
                        ) : (sd.basis && <span className="text-xs text-slate-500 ml-2">({sd.basis})</span>)}
                        {sd.expense_id && (
                          <button type="button" data-testid={`my-pay-view-receipt-${i}`} onClick={() => openReceipt(sd.expense_id)} className="inline-flex items-center gap-1 ml-2 text-xs text-[#002FA7] hover:underline"><Eye className="w-3 h-3" /> receipt</button>
                        )}
                      </span>
                      <span className="text-orange-700 font-semibold">− {fmtMoney(sd.amount)}</span>
                    </div>
                  ))}
                  {current.admin_fee_reason && !Number(current.admin_fee_amount) && (
                    <div data-testid="current-admin-fee-zero-row" className="flex items-center justify-between text-sm py-1.5 border-b border-slate-100 last:border-0" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      <span className="text-slate-700">Administration Fee<span className="text-xs text-slate-500 ml-2">({current.admin_fee_detail?.explanation || current.admin_fee_reason.replace(/_/g, ' ')})</span></span>
                      <span className="text-slate-500 font-semibold">$0.00</span>
                    </div>
                  )}
                  {(current.advance_repayments || []).map((ad, i) => (
                    <div key={`ad-${i}`} data-testid={`current-advance-row-${i}`} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-100 last:border-0" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      <span className="text-slate-700">
                        Cash Advance Repayment
                        {ad.reason && <span className="text-xs text-slate-500 ml-2">— {ad.reason}</span>}
                        <span className="text-xs text-slate-500 ml-2">(balance after: {fmtMoney(ad.remaining_after)})</span>
                      </span>
                      <span className="text-orange-700 font-semibold">− {fmtMoney(ad.repaid_this_run)}</span>
                    </div>
                  ))}
                  {(current.adjustments || []).map((adj, i) => (
                    <div key={`adj-${i}`} data-testid={`current-adjustment-row-${i}`} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-100 last:border-0" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      <span className="text-slate-700">
                        {adj.adjustment_type === 'compensation' ? 'Bonus / Compensation' : (adj.expense_category ? (EXPENSE_CATEGORY_LABEL[adj.expense_category] || adj.expense_category) : 'Custom Deduction')}
                        {adj.reason && <span className="text-xs text-slate-500 ml-2">— {adj.reason}</span>}
                      </span>
                      <span className={adj.adjustment_type === 'compensation' ? 'text-blue-700 font-semibold' : 'text-orange-700 font-semibold'}>
                        {adj.adjustment_type === 'compensation' ? '+ ' : '− '}{fmtMoney(adj.amount)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div data-testid="no-current-period" className="bg-white border border-dashed border-slate-300 rounded-md p-8 text-center mb-8">
            <Truck className="w-10 h-10 text-slate-400 mx-auto mb-2" />
            <p className="text-sm font-semibold text-slate-700" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>No pay periods yet</p>
            <p className="text-xs text-slate-500 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Your first paystub will appear here once your company runs payroll.</p>
          </div>
        )}

        {/* PAST PERIODS */}
        {entries.length > 1 && (
          <div className="bg-white border border-slate-200 rounded-md p-6">
            <h2 className="text-xl font-semibold tracking-tight text-slate-900 mb-4" style={{ fontFamily: 'Work Sans, sans-serif' }}>Past Pay Periods ({entries.length - 1})</h2>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                <thead>
                  <tr className="border-b border-slate-200 text-xs uppercase tracking-widest text-slate-500">
                    <th className="text-left py-2 font-semibold">Period</th>
                    <th className="text-right py-2 font-semibold">Loads</th>
                    <th className="text-right py-2 font-semibold">Gross</th>
                    <th className="text-right py-2 font-semibold">Deductions</th>
                    <th className="text-right py-2 font-semibold">Net</th>
                    <th className="text-right py-2 font-semibold">Paystub</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.slice(1).map((e) => (
                    <tr key={e.entry_id} data-testid={`past-entry-${e.entry_id}`} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/my-pay/${e.entry_id}`)}>
                      <td className="py-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-slate-900">{e.week_start} → {e.week_end}</p>
                          {correctionBadgeFor(e.entry_id)}
                        </div>
                        {e.generated_at && (
                          <p className="text-[11px] text-slate-500 mt-0.5">Run {new Date(e.generated_at).toLocaleString()}</p>
                        )}
                      </td>
                      <td className="py-3 text-right text-slate-700">{e.load_count}</td>
                      <td className="py-3 text-right text-slate-900 font-medium">{fmtMoney(e.gross_pay)}</td>
                      <td className="py-3 text-right text-orange-700">{fmtMoney((e.standing_deductions_total || 0) + (e.advance_repayments_total || 0) + (e.custom_deduction_total || 0))}</td>
                      <td className={`py-3 text-right font-bold ${netColor(e.net_pay)}`}>{fmtMoney(e.net_pay)}</td>
                      <td className="py-3 text-right">
                        <button
                          data-testid={`download-past-paystub-${e.entry_id}`}
                          onClick={(ev) => { ev.stopPropagation(); handleDownloadPaystub(e); }}
                          disabled={downloadingId === e.entry_id}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#002FA7] border border-[#002FA7] rounded-md px-3 py-1.5 hover:bg-blue-50 disabled:opacity-50 transition-colors"
                        >
                          <Download className="w-3.5 h-3.5" />
                          PDF
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="md:hidden space-y-3" data-testid="past-entries-mobile-list">
              {entries.slice(1).map((e) => (
                <div key={e.entry_id} data-testid={`past-entry-mobile-${e.entry_id}`} className="border border-slate-200 rounded-md p-3 cursor-pointer" onClick={() => navigate(`/my-pay/${e.entry_id}`)}>
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{e.week_start} → {e.week_end}</p>
                        {correctionBadgeFor(e.entry_id)}
                      </div>
                      <p className="text-xs text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{e.load_count} load{e.load_count === 1 ? '' : 's'}{e.generated_at ? ` · ${new Date(e.generated_at).toLocaleDateString()}` : ''}</p>
                    </div>
                    <button
                      data-testid={`download-past-paystub-mobile-${e.entry_id}`}
                      onClick={(ev) => { ev.stopPropagation(); handleDownloadPaystub(e); }}
                      disabled={downloadingId === e.entry_id}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#002FA7] border border-[#002FA7] rounded-md px-3 py-1.5 hover:bg-blue-50 disabled:opacity-50"
                    >
                      <Download className="w-3.5 h-3.5" />
                      PDF
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div><p className="text-[10px] uppercase tracking-widest text-slate-500">Gross</p><p className="text-sm font-bold text-slate-900">{fmtMoney(e.gross_pay)}</p></div>
                    <div><p className="text-[10px] uppercase tracking-widest text-slate-500">Ded.</p><p className="text-sm font-bold text-orange-700">{fmtMoney((e.standing_deductions_total || 0) + (e.advance_repayments_total || 0) + (e.custom_deduction_total || 0))}</p></div>
                    <div><p className="text-[10px] uppercase tracking-widest text-slate-500">Net</p><p className={`text-sm font-bold ${netColor(e.net_pay)}`}>{fmtMoney(e.net_pay)}</p></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default MyPay;
